<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  gaiaAccent,
  gaiaFontSize,
  gaiaTracking,
  gaiaFontWeight,
  gaiaNeutral,
  gaiaStatus,
  gaiaTemplating,
} from '@/components/design/themes/tokens';
import { NButton, NIcon, NTooltip } from 'naive-ui';
import {
  IconAdd,
  IconBusiness,
  IconCheckCircleFilled,
  IconClose,
  IconInfo,
  IconSearch,
  IconUpload,
  IconUser,
} from '@/components/design/icons';
import { TemplateFieldType, type TemplateField } from '@/api';
import {
  ENTITY_DIRECTORY_REQUIRED_KEYS,
  EntityFieldService,
  SIGNER_CONTACT_KEYS,
  changedPartyKeys,
  describeEntity,
  entityKindLabel,
  entityToFormValue,
  joinAnd,
  missingSignerContact,
  partyCache,
  slotPartyValues,
  slotSignerGap,
  type EntityFieldKind,
  type EntityRecord,
} from '@/services/contractGeneration/entityFieldService';
import SchemaField from './SchemaField.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import {
  useTemplateFieldMetadata,
  useTemplateFieldMetadataMap,
} from '@/composables/contractGeneration/useTemplateFieldMetadata';
import {
  humanizeKey,
  orderSiblings,
  resolveFieldLabel,
  resolveFieldProse,
} from '@/utils/contractGeneration/templateFieldLabels';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDropdown from '@/components/design/base/GaiaDropdown.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaStatusChip from '@/components/design/base/GaiaStatusChip.vue';
import GaiaRemoteSelect, {
  type GaiaRemoteFetch,
  type GaiaRemotePage,
} from '@/components/design/base/selects/GaiaRemoteSelect.vue';
import {
  preselectedRepresentative,
  searchPartiesOfKind,
  toPartyRow,
  type ContractualParty,
} from '@/services/contractManagement/contractualPartyRow';
import EntityFormDialog from '@/views/contractManagement/EntityFormDialog.vue';
import {
  signingPartyConfig,
  type EntityFormValues,
  type EntityRow,
} from '@/views/contractManagement/entityListConfig';
import { logger } from '@/utils/logger';
import { ACCESS_PERMISSION_WRITE, deniedReason } from '@/utils/accessPermission';
import FieldGuidanceHint from './FieldGuidanceHint.vue';
import SelectEntityModal, {
  type SelectableEntity,
} from '@/components/contractManagement/SelectEntityModal.vue';

interface Props {
  fieldKey: string;
  field: TemplateField;
  modelValue: unknown;
  rootFormData: Record<string, unknown>;
  localScopes?: unknown[];
  path: string;
  invalidPaths: Set<string>;
  disabled?: boolean;
  hideContainerHeader?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: Record<string, unknown> | null];
}>();

const message = useGaiaToast();

const entityKind = computed<EntityFieldKind>(() =>
  props.field.type === TemplateFieldType.NATURAL_PERSON ? 'natural_person' : 'legal_entity'
);

const childFields = computed(() => props.field.fields ?? {});

// Children in the author's order; the group's parent is this entity's path.
const metadataMap = useTemplateFieldMetadataMap();
const orderedChildren = computed(() =>
  orderSiblings(Object.entries(childFields.value), props.path, metadataMap.value)
);

const currentRecord = computed<Record<string, unknown>>(() => {
  const value = props.modelValue;
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
});

const hasAnyValue = computed<boolean>(() =>
  Object.values(currentRecord.value).some(value => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    return true;
  })
);

// Keys the directory's create endpoint requires that this template doesn't
// even ask the user for. If non-empty, we can never produce a valid create
// payload from manual input — the save banner has to stay hidden.
const missingDirectoryKeys = computed<string[]>(() => {
  const required = ENTITY_DIRECTORY_REQUIRED_KEYS[entityKind.value];
  const present = childFields.value;
  return required.filter(key => !(key in present));
});

const templateSupportsSave = computed<boolean>(() => missingDirectoryKeys.value.length === 0);

// Required-but-blank keys (template asks for them, user hasn't filled them in).
const missingFilledRequiredKeys = computed<string[]>(() => {
  const required = ENTITY_DIRECTORY_REQUIRED_KEYS[entityKind.value];
  return required.filter(key => {
    if (!(key in childFields.value)) return false;
    const value = currentRecord.value[key];
    if (typeof value === 'string') return value.trim().length === 0;
    return value == null;
  });
});

const directoryRequiredKeysFilled = computed<boolean>(
  () => missingFilledRequiredKeys.value.length === 0
);

function humanizeKeys(keys: readonly string[]): string {
  return keys.map(humanizeKey).join(', ');
}

// A value picked from (or saved to) the directory carries the party's `uid`,
// stored with the draft: finalize links that party to the contract and, on a
// signing slot, makes it a signer. Typed values carry none and link nothing.
const pickedEntityUid = computed<string | null>(() =>
  typeof currentRecord.value.uid === 'string' ? currentRecord.value.uid : null
);

// The picked party itself, for its name and — a legal entity signing — its
// representatives. Loaded from the uid (into the shared cache the signing
// readiness reads) so a reopened draft has it too.
const pickedParty = computed<ContractualParty | null>(() =>
  pickedEntityUid.value ? (partyCache.get(pickedEntityUid.value) ?? null) : null
);
watch(
  pickedEntityUid,
  async uid => {
    if (!uid) return;
    try {
      await EntityFieldService.getParty(uid);
      if (pickedEntityUid.value !== uid) return;
      // The default (or only) representative is preselected; still changeable.
      const preselected = pickedParty.value && preselectedRepresentative(pickedParty.value);
      if (
        signsThroughRepresentative.value &&
        preselected &&
        !currentRecord.value.representative_uid &&
        !props.disabled
      )
        void pickRepresentative(preselected.uid);
    } catch (error) {
      logger.warn('Could not load the picked contractual party:', error);
    }
  },
  { immediate: true }
);

const pickedEntityName = computed<string | null>(() =>
  pickedEntityUid.value ? describeEntity(pickedParty.value ?? currentRecord.value) : null
);

// ── Signing ─────────────────────────────────────────────────────────────────
// A signing slot's party signs the document when it is sent for signing; a
// legal entity signs through a person — one of its representatives, or anyone
// picked or created here, who is made a representative on the way. A slot
// typed by hand is saved as a party when the document is sent, so nothing has
// to be filed in advance; what signing needs is asked for right here.
const isSigner = computed(() => Boolean(props.field.signer));
const signsThroughRepresentative = computed(
  () => isSigner.value && entityKind.value === 'legal_entity'
);
const representatives = computed(() =>
  pickedParty.value?.kind === 'legal_entity' ? (pickedParty.value.representatives ?? []) : []
);
const representativeOptions = computed(() =>
  representatives.value.map(person => ({
    label: describeEntity(person as Record<string, unknown>),
    value: person.uid,
  }))
);

const representativeUid = computed<string | null>(() =>
  typeof currentRecord.value.representative_uid === 'string'
    ? currentRecord.value.representative_uid
    : null
);
watch(
  representativeUid,
  uid => {
    if (uid) void EntityFieldService.getParty(uid).catch(() => undefined);
  },
  { immediate: true }
);

/** The person whose contact details the provider needs: the party, or who signs for it. */
const signerPerson = computed<ContractualParty | null>(() => {
  if (entityKind.value === 'legal_entity') {
    return representativeUid.value ? (partyCache.get(representativeUid.value) ?? null) : null;
  }
  return pickedParty.value;
});
/**
 * Picking a party only needs READ; changing it needs WRITE on the record that
 * changes — the signing person for their details, the picked company for a
 * new representative, the source party for "Update". Null when allowed.
 */
const fixSignerBlock = computed(() =>
  deniedReason(signerPerson.value?.permissions, ACCESS_PERMISSION_WRITE)
);
const addRepresentativeBlock = computed(() =>
  pickedEntityUid.value
    ? deniedReason(pickedParty.value?.permissions, ACCESS_PERMISSION_WRITE)
    : null
);
const signerPersonMissing = computed<string[]>(() =>
  signerPerson.value ? missingSignerContact(signerPerson.value) : []
);

/** Ready, or the one thing still in the way — the same rule the finish button reads. */
const signerGap = computed<string | null>(() =>
  isSigner.value ? slotSignerGap(entityKind.value, props.field, currentRecord.value) : null
);

/**
 * A person typed into a natural-person signing slot: the contact keys signing
 * needs that this template doesn't ask for get their own inputs here, kept in
 * the slot's value and saved with the party.
 */
const extraContactKeys = computed<string[]>(() =>
  isSigner.value && entityKind.value === 'natural_person' && !pickedEntityUid.value
    ? SIGNER_CONTACT_KEYS.filter(key => !(key in childFields.value))
    : []
);
const contactLabels: Record<string, string> = {
  first_name: 'First name',
  last_name: 'Last name',
  email: 'Email for signing',
};

function updateExtraContact(key: string, value: string): void {
  emit('update:modelValue', { ...currentRecord.value, [key]: value });
}

/**
 * The "Signs through" options: the entity's own representatives — plus, for
 * an entity typed by hand (no representatives yet), the person chosen for it,
 * who becomes its representative when it is saved on send.
 */
const signerOptions = computed(() => {
  const uid = representativeUid.value;
  const chosen = uid ? partyCache.get(uid) : null;
  const listed = representativeOptions.value.some(option => option.value === uid);
  return chosen && !listed
    ? [...representativeOptions.value, { label: chosen.name, value: chosen.uid }]
    : representativeOptions.value;
});

const REPRESENTATIVE_ACTIONS = [
  { key: 'link', label: 'Add existing person' },
  { key: 'create', label: 'Create new person' },
];

function handleRepresentativeAction(key: string | number): void {
  if (key === 'link') openLinkPerson();
  else openNewSigner();
}

// ── Link an existing person (who then signs) ───────────────────────────────
const linkOpen = ref(false);
const linkPersonUid = ref<string | null>(null);

/** Natural persons who aren't this entity's representatives yet. */
const linkCandidates = computed<GaiaRemoteFetch>(() => {
  const searchPeople = searchPartiesOfKind('natural_person');
  const linked = new Set(representativeOptions.value.map(option => option.value));
  return async (query, page): Promise<GaiaRemotePage> => {
    const result = await searchPeople(query, page);
    return {
      ...result,
      options: result.options.filter(option => !linked.has(String(option.value))),
    };
  };
});

function openLinkPerson(): void {
  linkPersonUid.value = null;
  linkOpen.value = true;
}

async function confirmLinkPerson(): Promise<void> {
  const uid = linkPersonUid.value;
  if (!uid) return;
  await EntityFieldService.getParty(uid).catch(() => undefined);
  await pickRepresentative(uid);
  linkOpen.value = false;
}

const linkingRepresentative = ref(false);

/**
 * Choose who signs for the legal entity. A picked entity gets the person as a
 * representative straight away; a typed one is linked when it is saved, on send.
 */
async function pickRepresentative(uid: string | null): Promise<void> {
  emit('update:modelValue', { ...currentRecord.value, representative_uid: uid });
  const entityUid = pickedEntityUid.value;
  if (!uid || !entityUid || representatives.value.some(person => person.uid === uid)) return;
  linkingRepresentative.value = true;
  try {
    await EntityFieldService.addRepresentative(entityUid, uid);
    message.success(`Linked as a representative of ${pickedEntityName.value ?? 'the company'}.`);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to link the representative.');
  } finally {
    linkingRepresentative.value = false;
  }
}

// ── The party drawer: a new signing person, or a signer's missing details ───
const formVisible = ref(false);
const formRow = ref<EntityRow | null>(null);
const formDefaults = ref<EntityFormValues>({});
const formLockedKeys = ref<string[]>([]);
const formDefaultOptions = ref<Record<string, { label: string; value: string }[]>>({});
type AfterSave = (row: EntityRow | undefined) => Promise<void>;
let afterSave: AfterSave | null = null;

/** A new person who signs for this legal entity (and represents it, if it exists yet). */
function openNewSigner(): void {
  const entityUid = pickedEntityUid.value;
  formRow.value = null;
  formDefaults.value = {
    kind: 'natural_person',
    ...(entityUid ? { represented_uids: [entityUid] } : {}),
  };
  formLockedKeys.value = entityUid ? ['kind', 'represented_uids'] : ['kind'];
  formDefaultOptions.value = entityUid
    ? { represented_uids: [{ label: pickedEntityName.value ?? '', value: entityUid }] }
    : {};
  afterSave = async (row): Promise<void> => {
    if (!row) return;
    await EntityFieldService.getParty(row.uid, true);
    if (entityUid) await EntityFieldService.getParty(entityUid, true);
    emit('update:modelValue', { ...currentRecord.value, representative_uid: row.uid });
  };
  formVisible.value = true;
}

/** Fill in what the signing person's record lacks (an email, a name part). */
function openFixSigner(): void {
  const person = signerPerson.value;
  if (!person) return;
  formRow.value = toPartyRow(person);
  formDefaults.value = {};
  formLockedKeys.value = [];
  formDefaultOptions.value = {};
  afterSave = async (): Promise<void> => {
    const updated = await EntityFieldService.getParty(person.uid, true);
    const entityUid = pickedEntityUid.value;
    if (entityKind.value === 'legal_entity') {
      // The entity embeds its representatives; re-read it so they match.
      if (entityUid) await EntityFieldService.getParty(entityUid, true);
      return;
    }
    // The slot's values were copied from this person — keep them in step.
    emit('update:modelValue', entityToFormValue(updated, Object.keys(childFields.value)));
  };
  formVisible.value = true;
}

async function handleFormSaved(row?: EntityRow): Promise<void> {
  try {
    await afterSave?.(row);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to refresh the signer.');
  }
}

// The header is this component's to draw (unless the parent draws it), so the
// author's label and prose for this entity render here.
const meta = useTemplateFieldMetadata(() => props.path);
const label = computed(() => resolveFieldLabel(meta.value, props.fieldKey));
const description = computed(() => resolveFieldProse(meta.value?.description));
const guidance = computed(() => resolveFieldProse(meta.value?.guidance));

const kindIcon = computed(() => (entityKind.value === 'natural_person' ? IconUser : IconBusiness));
const kindLabel = computed(() => entityKindLabel(entityKind.value));

// ── Picker modal ────────────────────────────────────────────────────────────
const pickerOpen = ref<boolean>(false);

function openPicker(): void {
  pickerOpen.value = true;
}

function handlePickerSelect(entity: SelectableEntity): void {
  // The picker only lists contractual parties of this field's kind (`party-kind`).
  const party = entity as EntityRecord;
  editedFromUid.value = null;
  emit('update:modelValue', entityToFormValue(party, Object.keys(childFields.value)));
}

// ── Edited from a picked party ──────────────────────────────────────────────
// Editing a picked party's values unlinks it (the document no longer matches
// the record), but the party is remembered so the edit can be written back to
// it — e.g. the email the template asks for and the record lacks — instead of
// only saved as a new one. Session-only: a reopened draft offers "Save as new".
const editedFromUid = ref<string | null>(null);
const editedFromParty = computed<ContractualParty | null>(() =>
  editedFromUid.value ? (partyCache.get(editedFromUid.value) ?? null) : null
);
const updatingSource = ref(false);
const updateSourceBlock = computed(() =>
  deniedReason(editedFromParty.value?.permissions, ACCESS_PERMISSION_WRITE)
);

async function updateSource(): Promise<void> {
  const party = editedFromParty.value;
  if (!party) return;
  const changed = changedPartyKeys(currentRecord.value, party);
  updatingSource.value = true;
  try {
    const updated = await EntityFieldService.updateParty(
      party.uid,
      entityKind.value,
      Object.fromEntries(changed.map(key => [key, currentRecord.value[key]]))
    );
    editedFromUid.value = null;
    emit('update:modelValue', { ...currentRecord.value, uid: updated.uid });
    message.success(`${updated.name} updated.`);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to update.');
  } finally {
    updatingSource.value = false;
  }
}

// ── Save-as-new ─────────────────────────────────────────────────────────────
const savingNew = ref<boolean>(false);

async function saveAsNew(): Promise<void> {
  if (!templateSupportsSave.value) return;
  if (!directoryRequiredKeysFilled.value) {
    message.warning(`Fill in ${humanizeKeys(missingFilledRequiredKeys.value)} before saving.`);
    return;
  }
  if (!hasAnyValue.value) {
    message.warning('Fill in at least one field before saving.');
    return;
  }
  savingNew.value = true;
  try {
    const created = await EntityFieldService.createParty(
      entityKind.value,
      slotPartyValues(currentRecord.value)
    );
    editedFromUid.value = null;
    emit('update:modelValue', entityToFormValue(created, Object.keys(childFields.value)));
    message.success(
      `${kindLabel.value === 'legal entity' ? 'Legal entity' : 'Natural person'} saved.`
    );
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to save.');
  } finally {
    savingNew.value = false;
  }
}

// ── Reset ───────────────────────────────────────────────────────────────────
function clearSelection(): void {
  const blank: Record<string, unknown> = {};
  for (const key of Object.keys(childFields.value)) blank[key] = '';
  editedFromUid.value = null;
  emit('update:modelValue', blank);
}

function handleChildUpdate(childKey: string, childValue: unknown): void {
  // Editing any field after picking → drop the "picked" link since the data
  // no longer matches the directory record (remembering the party, so the
  // edit can update it). Typing the record's values back re-links it. Who
  // signs for a legal entity stays: a person of its own, linked to whichever
  // entity is saved.
  const record = { ...currentRecord.value, [childKey]: childValue };
  if (typeof record.uid === 'string') {
    editedFromUid.value = record.uid;
    // The signing contact the template doesn't ask for (the email) came from
    // the record; carry it over, or the person suddenly "needs" it.
    const picked = partyCache.get(record.uid) as Record<string, unknown> | undefined;
    if (picked && entityKind.value === 'natural_person') {
      for (const key of SIGNER_CONTACT_KEYS) {
        if (!(key in record) && picked[key]) record[key] = picked[key];
      }
    }
  }
  delete record.uid;
  const source = editedFromParty.value;
  if (source && changedPartyKeys(record, source).length === 0) {
    record.uid = source.uid;
    editedFromUid.value = null;
  }
  emit('update:modelValue', record);
}

// --- chrome (tokens for the scoped styles) ---
const fsBody = gaiaFontSize.body;
const fwMedium = String(gaiaFontWeight.medium);
const fwSemibold = String(gaiaFontWeight.semibold);
const fwBold = String(gaiaFontWeight.bold);
const trackCaps = gaiaTracking.labelSoft;
const fsMeta = gaiaFontSize.meta;
const fsRow = gaiaFontSize.row;
const gAccent = gaiaAccent.base;
const gAccentBorder = gaiaAccent.border;
const gAccentStrong = gaiaAccent.strong;
const gAccentTint = gaiaAccent.tint;
const gAccentWash = gaiaAccent.wash;
const gAlert = gaiaStatus.alert;
const gBorder = gaiaNeutral.borderCard;
const gBorderStrong = gaiaNeutral.border;
const gEntityBorder = gaiaTemplating.entityBorder;
const gEntityFg = gaiaTemplating.entityFg;
const gEntitySurface = gaiaTemplating.entitySurface;
const gFaint = gaiaNeutral.textFaint;
const gInk = gaiaNeutral.ink;
const gSoft = gaiaNeutral.surfaceSoft;
const gSubtle = gaiaNeutral.textSubtle;
const gSuccess = gaiaStatus.successText;
const gSurface = gaiaNeutral.surface;
const gText = gaiaNeutral.text;
</script>

<template>
  <div class="entity-field" :class="{ 'entity-field--picked': pickedEntityUid !== null }">
    <header v-if="!hideContainerHeader" class="entity-field__header">
      <div class="entity-field__title">
        <span class="entity-field__title-text">{{ label }}</span>
        <span v-if="field.required" class="entity-field__required">*</span>
        <field-guidance-hint v-if="guidance" :text="guidance" />
        <span class="entity-field__kind">
          <NIcon :size="12" class="entity-field__kind-icon">
            <component :is="kindIcon" />
          </NIcon>
          {{ kindLabel }}
        </span>
      </div>
    </header>
    <gaia-text
      v-if="!hideContainerHeader && description"
      size="meta"
      tone="subtle"
      :style="{ lineHeight: 1.45 }"
    >
      {{ description }}
    </gaia-text>

    <div class="entity-field__toolbar">
      <button
        type="button"
        class="search-trigger"
        :class="`search-trigger--${entityKind}`"
        :disabled="disabled"
        @click="openPicker"
      >
        <span class="search-trigger__main">
          <NIcon :size="16" class="search-trigger__icon">
            <IconSearch />
          </NIcon>
          <span class="search-trigger__label">
            {{ pickedEntityUid ? `Replace ${pickedEntityName ?? 'selection'}` : 'Add existing' }}
          </span>
        </span>
        <span class="search-trigger__pill">
          {{ pickedEntityUid ? 'Replace' : 'Browse' }}
        </span>
      </button>
      <NTooltip v-if="hasAnyValue" placement="top">
        <template #trigger>
          <NButton size="small" quaternary circle :disabled="disabled" @click="clearSelection">
            <template #icon>
              <NIcon><IconClose /></NIcon>
            </template>
          </NButton>
        </template>
        Clear all fields
      </NTooltip>
    </div>

    <div v-if="pickedEntityUid && pickedEntityName" class="entity-field__chip">
      <NIcon :size="14" class="entity-field__chip-icon">
        <IconCheckCircleFilled />
      </NIcon>
      <span class="entity-field__chip-text">
        Using <strong>{{ pickedEntityName }}</strong> — edit the fields below to override.
      </span>
    </div>

    <div class="entity-field__children">
      <SchemaField
        v-for="([childKey, childField], childIndex) in orderedChildren"
        :key="childKey"
        :field-key="childKey"
        :field="childField"
        :model-value="currentRecord[childKey]"
        :root-form-data="rootFormData"
        :local-scopes="[currentRecord, ...(localScopes ?? [])]"
        :path="`${path}.${childKey}`"
        :invalid-paths="invalidPaths"
        :disabled="disabled"
        :style="{ '--schema-field-delay': `${childIndex * 80}ms` }"
        @update:model-value="value => handleChildUpdate(childKey, value)"
      />
    </div>

    <!-- Signing: who signs, and what the provider still needs from them. -->
    <div v-if="isSigner" class="signer-panel">
      <n-flex vertical :size="10">
        <n-flex align="center" justify="space-between" :size="8" :wrap="false">
          <gaia-text size="dense" weight="semibold" tone="ink">Signer</gaia-text>
          <gaia-status-chip :tone="signerGap ? 'warning' : 'success'">
            {{ signerGap ? 'Needs details' : 'Ready to sign' }}
          </gaia-status-chip>
        </n-flex>

        <template v-if="signsThroughRepresentative">
          <gaia-form-field label="Signs through" required>
            <!-- Adding a representative lives beside the label, not inside the
                 list — as on the signing wizard's recipient cards. -->
            <template v-if="signerOptions.length > 0 && !disabled" #action>
              <gaia-dropdown
                :options="REPRESENTATIVE_ACTIONS"
                placement="bottom-end"
                :disabled="addRepresentativeBlock !== null"
                @select="handleRepresentativeAction"
              >
                <gaia-icon-button
                  :dimension="22"
                  quaternary
                  :title="addRepresentativeBlock ?? 'Add a representative'"
                  :disabled="addRepresentativeBlock !== null"
                >
                  <n-icon :component="IconAdd" :size="14" />
                </gaia-icon-button>
              </gaia-dropdown>
            </template>
            <gaia-select
              v-if="signerOptions.length > 0"
              :value="representativeUid"
              :options="signerOptions"
              :loading="linkingRepresentative"
              :disabled="disabled || linkingRepresentative"
              placeholder="Pick the representative who signs"
              style="width: 100%"
              @update:value="(value: string | null) => pickRepresentative(value)"
            />
            <!-- Nobody to pick yet: say so, and offer both ways to add one. -->
            <n-flex v-else vertical :size="8">
              <gaia-text size="meta" tone="subtle" :style="{ lineHeight: 1.45 }">
                {{
                  addRepresentativeBlock
                    ? `${pickedEntityName ?? 'This company'} has no representative yet, and you can only view it — ask someone with edit access to add one.`
                    : pickedEntityUid
                      ? `${pickedEntityName ?? 'This company'} has no representative yet. Add the person who signs for it.`
                      : 'Add the person who signs for this company — they become its representative when it is saved.'
                }}
              </gaia-text>
              <n-flex v-if="!addRepresentativeBlock" :size="8">
                <gaia-button size="small" :disabled="disabled" @click="openLinkPerson">
                  Add existing person
                </gaia-button>
                <gaia-button size="small" :disabled="disabled" @click="openNewSigner">
                  Create new person
                </gaia-button>
              </n-flex>
            </n-flex>
          </gaia-form-field>
        </template>

        <!-- A typed person: what signing needs that the template doesn't ask. -->
        <gaia-form-field
          v-for="key in extraContactKeys"
          :key="key"
          :label="contactLabels[key] ?? key"
          required
        >
          <gaia-input
            :value="(currentRecord[key] as string | undefined) ?? ''"
            :disabled="disabled"
            :placeholder="key === 'email' ? 'name@example.com' : ''"
            style="width: 100%"
            @update:value="(value: string) => updateExtraContact(key, value)"
          />
        </gaia-form-field>

        <n-flex
          v-if="signerPerson && signerPersonMissing.length > 0"
          align="center"
          justify="space-between"
          :size="8"
          :wrap="false"
        >
          <gaia-text size="meta" tone="subtle" :style="{ lineHeight: 1.45 }">
            {{ signerPerson.name }} still needs {{ joinAnd(signerPersonMissing) }} on record to
            sign.
            <template v-if="fixSignerBlock">
              You can only view them — ask someone with edit access.
            </template>
          </gaia-text>
          <gaia-button
            v-if="!fixSignerBlock"
            size="small"
            :disabled="disabled"
            @click="openFixSigner"
          >
            Add details
          </gaia-button>
        </n-flex>
        <gaia-text v-else-if="signerGap" size="meta" tone="subtle" :style="{ lineHeight: 1.45 }">
          To sign, {{ signerGap }}.
        </gaia-text>
        <gaia-text
          v-else-if="pickedEntityUid"
          size="meta"
          tone="subtle"
          :style="{ lineHeight: 1.45 }"
        >
          Signs this document when it is sent for signing.
        </gaia-text>
        <!-- Typed signers aren't saved up front: say where they end up. -->
        <gaia-text
          v-if="!pickedEntityUid && !editedFromParty && hasAnyValue"
          size="meta"
          tone="subtle"
          :style="{ lineHeight: 1.45 }"
        >
          Saved to your {{ kindLabel }} directory when the document is sent for signing.
        </gaia-text>
      </n-flex>
    </div>

    <transition name="save-banner-fade">
      <div
        v-if="
          !pickedEntityUid &&
          hasAnyValue &&
          (editedFromParty || (!isSigner && templateSupportsSave))
        "
        class="save-banner"
        :class="`save-banner--${entityKind}`"
      >
        <div class="save-banner__main">
          <div class="save-banner__icon">
            <NIcon :size="16"><IconUpload /></NIcon>
          </div>
          <div class="save-banner__text">
            <span v-if="editedFromParty" class="save-banner__title">
              Update {{ editedFromParty.name }}?
            </span>
            <span v-else class="save-banner__title">Save to your {{ kindLabel }} directory</span>
            <span class="save-banner__sub">
              {{
                editedFromParty
                  ? updateSourceBlock
                    ? 'You can only view this record — save the changes as a new one instead.'
                    : 'Write these changes to the record, or save them as a new one.'
                  : 'Reusable across future contracts.'
              }}
            </span>
          </div>
        </div>
        <NButton
          v-if="editedFromParty"
          type="primary"
          size="small"
          class="save-banner__cta"
          :loading="updatingSource"
          :disabled="disabled || savingNew || updateSourceBlock !== null"
          :title="updateSourceBlock ?? undefined"
          @click="updateSource"
        >
          Update
        </NButton>
        <NTooltip
          v-if="templateSupportsSave"
          :disabled="directoryRequiredKeysFilled"
          placement="top"
        >
          <template #trigger>
            <span class="save-banner__cta-wrap">
              <NButton
                :type="editedFromParty ? 'default' : 'primary'"
                size="small"
                class="save-banner__cta"
                :loading="savingNew"
                :disabled="disabled || !directoryRequiredKeysFilled"
                @click="saveAsNew"
              >
                Save as new
              </NButton>
            </span>
          </template>
          Fill in {{ humanizeKeys(missingFilledRequiredKeys) }} before saving.
        </NTooltip>
      </div>
    </transition>

    <p v-if="!pickedEntityUid && hasAnyValue && !templateSupportsSave" class="save-disabled-note">
      <NIcon :size="12" class="save-disabled-note__icon">
        <IconInfo />
      </NIcon>
      This template doesn't ask for {{ humanizeKeys(missingDirectoryKeys) }}, so this
      {{ kindLabel }} can't be saved to your directory. You can still pick an existing one above.
    </p>

    <!-- Link an existing natural person as a representative, who then signs. -->
    <gaia-modal
      v-if="signsThroughRepresentative"
      :show="linkOpen"
      width="460px"
      :title="`Add a representative of ${pickedEntityName ?? 'this company'}`"
      @update:show="(open: boolean) => (linkOpen = open)"
    >
      <n-flex vertical :size="10" :wrap="false">
        <gaia-text size="control" tone="text">
          Pick a natural person. They're linked as a representative and sign for this entity.
        </gaia-text>
        <gaia-remote-select
          v-model:value="linkPersonUid"
          :fetch="linkCandidates"
          prime-on-mount
          placeholder="Search people"
        />
      </n-flex>
      <template #action>
        <n-flex justify="end" :size="8">
          <gaia-button @click="linkOpen = false">Cancel</gaia-button>
          <gaia-button
            type="primary"
            :disabled="!linkPersonUid"
            :loading="linkingRepresentative"
            @click="confirmLinkPerson"
          >
            Link and sign
          </gaia-button>
        </n-flex>
      </template>
    </gaia-modal>

    <entity-form-dialog
      v-if="isSigner"
      v-model:show="formVisible"
      :config="signingPartyConfig"
      :row="formRow"
      :row-name="formRow ? String(formRow.name ?? '') : ''"
      :defaults="formDefaults"
      :locked-keys="formLockedKeys"
      :default-options="formDefaultOptions"
      @saved="handleFormSaved"
    />

    <SelectEntityModal
      v-model:show="pickerOpen"
      entity-type="contractual-party"
      :party-kind="entityKind"
      mode="pick"
      @selected="handlePickerSelect"
    />
  </div>
</template>

<style scoped>
.entity-field {
  display: flex;
  flex-direction: column;
  gap: 12px;
  border: 1px solid v-bind(gBorder);
  border-radius: 10px;
  padding: 14px 16px;
  background: v-bind(gSoft);
  transition: border-color 0.2s ease;
}

.entity-field--picked {
  border-color: v-bind(gAccentBorder);
  background: linear-gradient(180deg, v-bind(gAccentWash) 0%, v-bind(gSoft) 60%);
}

.entity-field__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.entity-field__title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.entity-field__title-text {
  font-weight: v-bind(fwSemibold);
  font-size: v-bind(fsBody);
  color: v-bind(gInk);
}

.entity-field__required {
  color: v-bind(gAlert);
  font-weight: v-bind(fwBold);
}

.entity-field__kind {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: v-bind(fsMeta);
  font-weight: v-bind(fwSemibold);
  letter-spacing: v-bind(trackCaps);
  text-transform: uppercase;
  color: v-bind(gAccent);
  background: v-bind(gAccentWash);
  padding: 2px 8px;
  border-radius: 999px;
}

.entity-field__kind-icon {
  display: inline-flex;
}

.entity-field__toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ── Search trigger (input-styled button that opens the picker) ─────────── */
.search-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex: 1 1 auto;
  min-width: 0;
  padding: 8px 8px 8px 12px;
  background: v-bind(gSurface);
  border: 1px solid v-bind(gBorderStrong);
  border-radius: 10px;
  font: inherit;
  color: v-bind(gText);
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    background-color 0.18s ease;
}

.search-trigger:hover:not(:disabled) {
  border-color: v-bind(gAccent);
  background: v-bind(gAccentWash);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}

.search-trigger:focus-visible {
  outline: none;
  border-color: v-bind(gAccent);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.search-trigger:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.search-trigger--natural_person:hover:not(:disabled) {
  border-color: v-bind(gAccent);
  background: v-bind(gAccentWash);
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.08);
}

.search-trigger--natural_person:focus-visible {
  border-color: v-bind(gAccent);
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
}

.search-trigger__main {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
}

.search-trigger__icon {
  color: v-bind(gFaint);
  flex-shrink: 0;
  transition: color 0.18s ease;
}

.search-trigger:hover:not(:disabled) .search-trigger__icon {
  color: v-bind(gAccent);
}

.search-trigger--natural_person:hover:not(:disabled) .search-trigger__icon {
  color: v-bind(gAccent);
}

.search-trigger__label {
  font-size: v-bind(fsRow);
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-trigger__pill {
  font-size: v-bind(fsMeta);
  font-weight: v-bind(fwSemibold);
  letter-spacing: v-bind(trackCaps);
  color: v-bind(gAccent);
  background: v-bind(gAccentWash);
  padding: 4px 10px;
  border-radius: 999px;
  flex-shrink: 0;
  transition:
    background-color 0.18s ease,
    color 0.18s ease;
}

.search-trigger--natural_person .search-trigger__pill {
  color: v-bind(gAccent);
  background: v-bind(gAccentWash);
}

.search-trigger:hover:not(:disabled) .search-trigger__pill {
  background: v-bind(gAccent);
  color: v-bind(gSurface);
}

.search-trigger--natural_person:hover:not(:disabled) .search-trigger__pill {
  background: v-bind(gAccent);
  color: v-bind(gSurface);
}

.entity-field__chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: v-bind(gEntitySurface);
  color: v-bind(gEntityFg);
  font-size: v-bind(fsRow);
  border: 1px solid v-bind(gEntityBorder);
}

.entity-field__chip-icon {
  color: v-bind(gSuccess);
}

.entity-field__chip-text {
  flex: 1 1 auto;
  min-width: 0;
  line-height: 1.45;
}

.entity-field__children {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* The signing panel: a white card inside the slot's tinted one — the slot's
   own surface and border, which no Naive prop sets. */
.signer-panel {
  padding: 12px;
  border: 1px solid v-bind(gBorder);
  border-radius: 8px;
  background: v-bind(gSurface);
}

/* ── Save-to-directory banner ──────────────────────────────────────────── */
/* Container-aware so the layout reflows when the Compose pane is narrow
   (preview open) regardless of viewport width. */
.save-banner {
  container-type: inline-size;
  container-name: save-banner;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid v-bind(gAccentTint);
  background: v-bind(gAccentWash);
}

.save-banner--natural_person {
  border-color: v-bind(gAccentTint);
  background: v-bind(gAccentWash);
}

.save-banner__main {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1 1 220px;
}

.save-banner__icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: v-bind(gAccent);
  color: v-bind(gSurface);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.save-banner--natural_person .save-banner__icon {
  background: v-bind(gAccent);
}

.save-banner__text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1 1 auto;
}

.save-banner__title {
  font-size: v-bind(fsRow);
  font-weight: v-bind(fwMedium);
  color: v-bind(gInk);
  line-height: 1.3;
}

.save-banner__sub {
  font-size: v-bind(fsMeta);
  color: v-bind(gSubtle);
  line-height: 1.4;
}

.save-banner__cta {
  flex-shrink: 0;
}

.save-banner--natural_person .save-banner__cta.n-button--primary-type {
  --n-color: v-bind(gAccent);
  --n-color-hover: v-bind(gAccentStrong);
  --n-color-pressed: v-bind(gAccentStrong);
  --n-color-focus: v-bind(gAccentStrong);
}

.save-banner-fade-enter-active,
.save-banner-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.save-banner-fade-enter-from,
.save-banner-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

/* Narrow container: stack body + CTA, full-width button. */
@container save-banner (max-width: 360px) {
  .save-banner__cta {
    width: 100%;
    justify-content: center;
  }
  .save-banner__main {
    flex: 1 1 100%;
  }
}

/* Very tight: hide the sub-line so title + button still fit cleanly. */
@container save-banner (max-width: 240px) {
  .save-banner__sub {
    display: none;
  }
}

/* "Template can't satisfy directory" subtle inline note */
.save-disabled-note {
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  margin: 6px 0 0;
  padding: 8px 10px;
  background: v-bind(gSoft);
  border: 1px dashed v-bind(gBorder);
  border-radius: 8px;
  font-size: v-bind(fsMeta);
  color: v-bind(gSubtle);
  line-height: 1.45;
}

.save-disabled-note__icon {
  color: v-bind(gFaint);
  margin-top: 2px;
  flex-shrink: 0;
}

/* Wrap span around the disabled NButton so NTooltip still triggers */
.save-banner__cta-wrap {
  display: inline-flex;
}
</style>
